/**
 * Publicly hosted integration guides for the PMS marketplaces.
 *
 * These are listing prerequisites rather than product documentation: Cloudbeds
 * will not schedule a certification call without a public URL for the guide,
 * and Mews accepts the same document for onboarding. Deliberately kept out of
 * `content/docs/` — that manifest models the dashboard's sidebar sections and
 * requires a quickstart and a manual tier per entry, neither of which fits a
 * single reviewer-facing article.
 *
 * One page per system, because reviewers check that the guide describes *their*
 * product specifically; a shared page with the PMS name swapped in reads as
 * boilerplate and has been rejected on that basis.
 *
 * Loaders are lazy `?raw` imports so a visitor to one guide does not download
 * the others, matching the docs manifest.
 */

export type PmsGuide = {
    /** URL segment under `/support/`. Cited in submission paperwork — do not rename casually. */
    slug: string;
    /** The PMS as its own vendor writes it, which is what reviewers look for. */
    system: string;
    title: string;
    summary: string;
    /** Vendor's own docs, linked so reviewers can cross-check our claims. */
    vendorDocsUrl: string;
    load: () => Promise<{ default: string }>;
};

export const PMS_GUIDES: PmsGuide[] = [
    {
        slug: "cloudbeds-integration",
        system: "Cloudbeds",
        title: "floGPT AI Receptionist for Cloudbeds",
        summary:
            "How to connect Cloudbeds to floGPT, what the AI voice agent can do with it, its limitations, and how to disconnect from either side.",
        vendorDocsUrl: "https://developers.cloudbeds.com/",
        load: () => import("./cloudbeds.md?raw"),
    },
    {
        slug: "mews-integration",
        system: "Mews",
        title: "floGPT AI Receptionist for Mews",
        summary:
            "How to connect Mews to floGPT, what the AI voice agent can do with it, its limitations, and how to disconnect from either side.",
        vendorDocsUrl: "https://mews-systems.gitbook.io/connector-api/",
        load: () => import("./mews.md?raw"),
    },
];

export const getPmsGuide = (slug: string | undefined): PmsGuide | undefined =>
    slug ? PMS_GUIDES.find((guide) => guide.slug === slug) : undefined;

export const pmsGuidePath = (slug: string): string => `/support/${slug}`;

/**
 * Whether `pathname` is one of these guides.
 *
 * Matches known slugs rather than the `/support/` prefix because the dashboard
 * already owns `/support/live-calls`; a prefix test would strand signed-in
 * users on a marketing page instead of their live-calls screen.
 */
export const isPmsGuidePath = (pathname: string): boolean => {
    const match = /^\/support\/([^/]+)\/?$/.exec(pathname);
    return Boolean(match && PMS_GUIDES.some((guide) => guide.slug === match[1]));
};
